import React, { useEffect, useMemo, useState } from 'react';
import i18n from '../../../../../i18n';
import { METRICS, fmt } from '../../../data/metrics';
import {
  SUB_CHANNEL_NONE_KEY,
  hasSourceChannels,
  sourceMeta,
  subChannelDisplayLabel,
} from '../../../data/reservationSources';
import type {
  ConnectedSourceId,
  SourceBreakdown,
  SourceKpis,
  SourceSubChannelKpis,
} from '../../../types';
import {
  bucketCoverTotals,
  coverShareDp,
  fmtSourceMetric,
  isCoverShareMetric,
  mixBarShareLabel,
  mixBarVolume,
  mixTrendUnit,
  pctOf,
  resolveSourceMixKeys,
  scaleCoverShare,
  scaleCoverShareSeries,
  sourceMetricSeries,
  sourceMetricValue,
} from '../../SourceMixPanel';
import { MiniTrend, trendStats } from '../../shared/Trend';
import { Icon } from '../../shared/Icon';

function shortMetric(key?: string): string {
  if (!key) return '';
  return METRICS[key]?.short ?? key;
}

function fmtFullCount(n: number): string {
  return fmt.num(n);
}

/** Compact count for dense rows: 956k, 3.2M — full value belongs in a tip. */
function fmtCompactCount(n: number): string {
  const v = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    const rounded = m >= 10 ? Math.round(m) : Math.round(m * 10) / 10;
    return `${sign}${rounded}M`;
  }
  if (v >= 1_000) {
    const k = v / 1_000;
    const rounded = k >= 10 ? Math.round(k) : Math.round(k * 10) / 10;
    return `${sign}${rounded}k`;
  }
  return `${sign}${Math.round(v)}`;
}

function ChannelList({
  parent,
  channels,
}: {
  parent: SourceKpis;
  channels: SourceSubChannelKpis[];
}) {
  const [showAll, setShowAll] = useState(false);
  const color = sourceMeta(parent.source).color;
  const ranked = useMemo(
    () => [...channels].sort((a, b) => b.covers - a.covers),
    [channels],
  );
  // Guest share within Widget. Sub-channel covers can exceed parent.covers when
  // rollups disagree — use max(parent, channel sum) so no row can show >100%.
  const coverDenom = Math.max(
    parent.covers,
    ranked.reduce((a, c) => a + c.covers, 0),
  );
  const visible = showAll ? ranked : ranked.slice(0, 4);
  const hidden = ranked.length - visible.length;

  return (
    <div className="smi-channels">
      <div className="smi-channels-lab">{i18n.t('performance:drillUi.channelsGuestShare')}</div>
      {visible.map(ch => {
        const rawLabel = subChannelDisplayLabel(parent.source, ch);
        const label =
          ch.key === SUB_CHANNEL_NONE_KEY || rawLabel === '(none)'
            ? i18n.t('performance:drillUi.noAttribution')
            : rawLabel;
        const share = pctOf(ch.covers, coverDenom);
        const barPct = Math.min(100, share);
        return (
          <div className="smi-channel" key={ch.key}>
            <span className="smi-channel-name" title={label}>
              <span
                className="smi-dot"
                style={{ background: color, opacity: 0.55 }}
              />
              {label}
            </span>
            <span className="smi-channel-track" aria-hidden>
              <span
                className="smi-channel-fill"
                style={{
                  width: `${Math.max(3, barPct)}%`,
                  background: color,
                }}
              />
            </span>
            <span
              className="smi-channel-meta"
              data-tip={i18n.t('performance:drillUi.guestsBookings', {
                guests: fmtFullCount(ch.covers),
                bookings: fmtFullCount(ch.reservations),
              })}
            >
              <b>{share}%</b>
              <span>
                {i18n.t('performance:drillUi.guestsBookings', {
                  guests: fmtCompactCount(ch.covers),
                  bookings: fmtCompactCount(ch.reservations),
                })}
              </span>
            </span>
          </div>
        );
      })}
      {hidden > 0 || showAll ? (
        <button
          type="button"
          className="smi-more"
          onClick={e => {
            e.stopPropagation();
            setShowAll(v => !v);
          }}
        >
          {showAll
            ? i18n.t('performance:drillUi.showFewer')
            : i18n.t('performance:drillUi.showMore', { count: hidden })}
        </button>
      ) : null}
    </div>
  );
}

function SourceCard({
  s,
  open,
  onToggle,
  metricKey,
  secondaryKey,
  moneyUnit,
  metricValue,
  metricSeries,
  bucketCovers,
  totalCovers,
  totalReservations,
  mixShare,
  mixShareLabel,
  bucketLabels,
  aggregationLabel,
  comparable,
  compareLabel,
}: {
  s: SourceKpis;
  open: boolean;
  onToggle: () => void;
  metricKey?: string;
  secondaryKey?: string;
  moneyUnit?: string | null;
  metricValue?: number;
  metricSeries?: number[];
  bucketCovers?: number[];
  totalCovers: number;
  totalReservations: number;
  mixShare: number;
  mixShareLabel: string;
  bucketLabels: string[];
  aggregationLabel?: string;
  comparable: boolean;
  compareLabel?: string;
}) {
  const m = sourceMeta(s.source);
  const mixKeys = resolveSourceMixKeys(metricKey, secondaryKey);
  const primaryKey = mixKeys.primary ?? metricKey;
  const secondary = mixKeys.secondary;
  const dp = coverShareDp(primaryKey);
  const periodScaled = isCoverShareMetric(primaryKey)
    ? scaleCoverShare(
        sourceMetricValue(s, primaryKey),
        metricValue,
        totalCovers,
        dp,
      )
    : null;
  const periodPrimary = periodScaled ?? sourceMetricValue(s, primaryKey);

  const rawSeries = sourceMetricSeries(s, primaryKey);
  const contribSeries = isCoverShareMetric(primaryKey)
    ? scaleCoverShareSeries(rawSeries, metricSeries, bucketCovers ?? [], dp)
    : rawSeries;
  const st =
    contribSeries && contribSeries.length > 1
      ? trendStats(contribSeries)
      : rawSeries && rawSeries.length > 1
        ? trendStats(rawSeries)
        : null;
  const hasCtx = !!st?.hasSeries;
  const periodSecondary = secondary
    ? sourceMetricValue(s, secondary)
    : undefined;
  const secondarySeries = secondary
    ? sourceMetricSeries(s, secondary)
    : undefined;
  const delta = comparable ? (s.deltaPct ?? null) : null;
  const deltaUnit = '%';
  const latestLabel = bucketLabels[bucketLabels.length - 1] ?? '';
  const vsLabel =
    compareLabel ||
    (bucketLabels.length > 1
      ? bucketLabels[bucketLabels.length - 2]
      : i18n.t('performance:drillUi.priorGrain', {
          grain: aggregationLabel ?? i18n.t('performance:drillUi.period'),
        }));
  const guestShare = pctOf(s.covers, totalCovers);
  const bookShare = pctOf(s.reservations, totalReservations);
  const hasChannels = hasSourceChannels(s.source) && !!s.subChannels?.length;

  const scaledSeries = contribSeries;

  return (
    <div className={`smi-card${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="smi-card-head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="smi-card-id">
          <span className="smi-dot" style={{ background: m.color }} />
          <span className="smi-card-name">{m.label}</span>
          <span className="smi-share-pill">{mixShare}%</span>
        </span>
        <span className="smi-card-head-right">
          {delta != null ? (
            <span
              className="smi-delta"
              style={{
                color: delta >= 0 ? 'var(--st-steady)' : 'var(--st-alert)',
              }}
            >
              {delta >= 0 ? '+' : ''}
              {delta}{deltaUnit}
            </span>
          ) : null}
          <span className={`smi-chevron${open ? ' is-open' : ''}`} aria-hidden>
            <Icon name="chevron" size={14} />
          </span>
        </span>
      </button>

      <div className="smi-card-summary">
        <div className="smi-fig">
          <strong>
            {fmtSourceMetric(periodPrimary, primaryKey, moneyUnit)}
          </strong>
          <span>{shortMetric(primaryKey)}</span>
        </div>
        {periodSecondary != null && secondary ? (
          <div className="smi-fig is-secondary">
            <strong>
              {fmtSourceMetric(periodSecondary, secondary, moneyUnit)}
            </strong>
            <span>{shortMetric(secondary)}</span>
          </div>
        ) : null}
      </div>

      {open ? (
        <div className="smi-card-body">
          <div className="smi-kv">
            <div className="smi-kv-row">
              <span>{i18n.t('performance:drillUi.mixShare')}</span>
              <b>
                {i18n.t('performance:drillUi.pctOf', {
                  pct: mixShare,
                  noun: mixShareLabel,
                })}
                {metricKey === 'reservations' || metricKey === 'covers'
                  ? ` · ${i18n.t('performance:drillUi.pctOf', {
                      pct: metricKey === 'reservations' ? guestShare : bookShare,
                      noun:
                        metricKey === 'reservations'
                          ? i18n.t('performance:drillUi.guests')
                          : i18n.t('performance:drillUi.bookings'),
                    })}`
                  : ''}
              </b>
            </div>
            {comparable && delta != null ? (
              <div className="smi-kv-row">
                <span>{i18n.t('performance:drillUi.vsLabel', { label: vsLabel })}</span>
                <b
                  style={{
                    color: delta >= 0 ? 'var(--st-steady)' : 'var(--st-alert)',
                  }}
                >
                  {delta >= 0 ? '+' : ''}
                  {delta}{deltaUnit}
                </b>
              </div>
            ) : null}
            {hasCtx && latestLabel ? (
              <div className="smi-kv-row">
                <span>
                  {i18n.t('performance:drillUi.latestGrain', {
                    grain: aggregationLabel ?? i18n.t('performance:drillUi.period'),
                  })}
                </span>
                <b>{latestLabel}</b>
              </div>
            ) : null}
          </div>

          {scaledSeries && scaledSeries.length > 1 ? (
            <div className="smi-trend">
              <MiniTrend
                values={scaledSeries}
                secondaryValues={secondarySeries}
                labels={bucketLabels}
                unit={
                  isCoverShareMetric(primaryKey) && !scaledSeries
                    ? undefined
                    : mixTrendUnit(primaryKey, moneyUnit)
                }
                secondaryUnit={mixTrendUnit(secondary, moneyUnit)}
                primaryLabel={shortMetric(primaryKey)}
                secondaryLabel={shortMetric(secondary)}
                compact
              />
            </div>
          ) : null}

          <div className="smi-chips">
            <span className="smi-chip">
              {i18n.t('performance:drillUi.noshowChip', { pct: s.noshow })}
            </span>
            <span className="smi-chip">
              {i18n.t('performance:drillUi.cancelChip', { pct: s.cancel })}
            </span>
            {s.seatTime > 0 ? (
              <span className="smi-chip">
                {i18n.t('performance:drillUi.seatChip', { mins: s.seatTime })}
              </span>
            ) : null}
          </div>

          {hasChannels ? (
            <ChannelList parent={s} channels={s.subChannels!} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Narrow operate-mode source mix for the Performance drill inspector. */
export function SourceMixInspector({
  breakdown,
  connected,
  bucketLabels,
  aggregationLabel,
  periodLabel,
  comparable = true,
  compareLabel,
  metricKey,
  secondaryKey,
  moneyUnit,
  metricValue,
  metricSeries,
}: {
  breakdown?: SourceBreakdown;
  connected?: ConnectedSourceId[];
  bucketLabels?: string[];
  aggregationLabel?: string;
  periodLabel?: string;
  comparable?: boolean;
  /** Legend label for the compare column, e.g. "vs 3 Jun – 30 Jun". */
  compareLabel?: string;
  metricKey?: string;
  secondaryKey?: string;
  moneyUnit?: string | null;
  metricValue?: number;
  /** Per-bucket fleet occupancy / turns, aligned with source cover series. */
  metricSeries?: number[];
}) {
  const sources = useMemo(() => {
    if (!breakdown?.sources?.length) return [];
    return [...breakdown.sources].sort(
      (a, b) => mixBarVolume(b, metricKey) - mixBarVolume(a, metricKey),
    );
  }, [breakdown?.sources, metricKey]);

  const topSource = sources[0]?.source ?? null;
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => {
    setOpenId(topSource);
  }, [topSource, metricKey]);

  if (!breakdown?.sources?.length || !breakdown.totalCovers) return null;

  const resolvedMoney =
    moneyUnit || breakdown.posUnit || breakdown.posCurrency || null;
  const mixKeys = resolveSourceMixKeys(metricKey, secondaryKey);
  const barLabel = mixBarShareLabel(metricKey);
  const totalMix = sources.reduce(
    (a, s) => a + mixBarVolume(s, metricKey),
    0,
  );
  const useCoverBar = totalMix <= 0;
  const mixTotal = useCoverBar ? breakdown.totalCovers : totalMix;
  const totalReservations = sources.reduce((a, s) => a + s.reservations, 0);
  const labels = bucketLabels ?? [];
  const latestLabel = labels[labels.length - 1];
  const coverTotals = bucketCoverTotals(sources);

  const segs = sources.map(s => {
    const vol = useCoverBar ? s.covers : mixBarVolume(s, metricKey);
    return {
      source: s.source,
      label: sourceMeta(s.source).label,
      color: sourceMeta(s.source).color,
      share: pctOf(vol, mixTotal),
      vol,
      compareShare: s.compareShare,
    };
  });
  const hasCompare = comparable && segs.some(s => s.compareShare != null);

  return (
    <div className="smi">
      <div className="smi-context">
        <div className="smi-bar" aria-hidden>
          {segs.map(seg =>
            seg.share > 0 ? (
              <span
                key={seg.source}
                className="smi-bar-seg"
                style={{ width: `${seg.share}%`, background: seg.color }}
                title={`${seg.label} · ${seg.share}%`}
              />
            ) : null,
          )}
        </div>
        <ul className="smi-legend">
          {segs
            .filter(s => s.share > 0)
            .map(seg => (
              <li key={seg.source}>
                <span
                  className="smi-dot"
                  style={{ background: seg.color }}
                  aria-hidden
                />
                <span>{seg.label}</span>
                <b>{seg.share}%</b>
                {hasCompare && seg.compareShare != null ? (
                  <span
                    className="dcrux-cmp-sub"
                    title={
                      compareLabel
                        ? `${compareLabel}: ${seg.compareShare}%`
                        : undefined
                    }
                  >
                    · {seg.compareShare}%
                    {(() => {
                      const pt = +(seg.share - seg.compareShare).toFixed(1);
                      return ` (${pt >= 0 ? '+' : ''}${pt}pt)`;
                    })()}
                  </span>
                ) : null}
              </li>
            ))}
        </ul>
        <p className="smi-meta">
          {periodLabel
            ? i18n.t('performance:drillUi.periodDot', { label: periodLabel })
            : comparable && latestLabel
              ? i18n.t('performance:drillUi.latestGrainDot', {
                  grain: aggregationLabel ?? i18n.t('performance:drillUi.period'),
                  label: latestLabel,
                })
              : i18n.t('performance:drillUi.shareOf', {
                  noun: useCoverBar ? i18n.t('performance:drillUi.guests') : barLabel,
                })}
          {mixKeys.primary && mixKeys.secondary
            ? ` · ${shortMetric(mixKeys.primary)} · ${shortMetric(mixKeys.secondary)}`
            : ''}
        </p>
      </div>

      <div className="smi-list">
        {sources.map(s => {
          const vol = useCoverBar ? s.covers : mixBarVolume(s, metricKey);
          const cardMix = pctOf(vol, mixTotal);
          return (
            <SourceCard
              key={s.source}
              s={s}
              open={openId === s.source}
              onToggle={() =>
                setOpenId(cur => (cur === s.source ? null : s.source))
              }
              metricKey={metricKey}
              secondaryKey={secondaryKey}
              moneyUnit={resolvedMoney}
              metricValue={metricValue}
              metricSeries={metricSeries}
              bucketCovers={coverTotals}
              totalCovers={breakdown.totalCovers}
              totalReservations={totalReservations}
              mixShare={cardMix}
              mixShareLabel={
                useCoverBar ? i18n.t('performance:drillUi.guests') : barLabel
              }
              bucketLabels={labels}
              aggregationLabel={aggregationLabel}
              comparable={comparable}
              compareLabel={compareLabel}
            />
          );
        })}
      </div>

      {connected?.includes('alenopay') ? (
        <p className="smi-foot">{i18n.t('performance:drillUi.includesAlenoPay')}</p>
      ) : null}
    </div>
  );
}
